const PlummeAccountingJournalPagination = (() => {
  const MAX_PAGES = 1000;
  const MAX_EVENTS_PER_PAGE = 500;
  const MAX_ACCUMULATED_BYTES = 128 * 1024 * 1024;
  const SNAPSHOT_RETRY_DELAYS_MS = [100, 250];
  const SOURCE_KEYS = [
    'accountingSourceVersions', 'openingBalanceByYear', 'invoices', 'invoiceInventory', 'expenses', 'payments', 'customerSettlements',
    'supplierSettlements', 'otherSales', 'customsDeclarations', 'payrollRuns', 'payrollTaxPaymentsByYear',
    'bankReconciliationPostings', 'mileageEntries', 'yearEndEntriesByYear', 'fixedAssets',
    'accountingImportReportStatsByYear', 'accountingImportSalesHistory', 'accountingImportSalesHistorySummary',
  ];
  const text = (value) => String(value ?? '').trim();

  const reloadSourceValue = (key, value) => key === 'invoices'
    ? (Array.isArray(value) ? value : []).filter((invoice) => text(invoice?.status).toLowerCase() !== 'draft' && !['offer', 'order_confirmation'].includes(text(invoice?.kind).toLowerCase()))
    : value;
  const reloadKey = (state, year) => JSON.stringify([
    Number(year), Number(state?.accountingRecordedSeqCounter || 0),
    ...SOURCE_KEYS.map((key) => reloadSourceValue(key, state?.[key] ?? null)),
  ]);

  // Approval responses omit journal hydration. Preserve its independent server
  // proof only for the same year, watermark and unchanged accounting sources.
  const mergeReadProof = (previous, merged, response) => {
    const year = Number(previous?.activeYear);
    if (Object.hasOwn(response, 'accountingEvents') || !Array.isArray(previous?.accountingEvents)
      || Number(merged.activeYear) !== year || reloadKey(merged, year) !== reloadKey(previous, year)) return merged;
    return { ...merged, accountingEvents: previous.accountingEvents,
      accountingJournalStatus: previous.accountingJournalStatus, accountingBasisCoverage: previous.accountingBasisCoverage };
  };

  const mergeMutationReadProof = (runtime, merged, response) => {
    const next = mergeReadProof(runtime.appState, merged, response);
    if (Object.hasOwn(response, 'accountingEvents') || next.accountingBasisCoverage === runtime.appState?.accountingBasisCoverage) return next;
    // Revalidate after the caller publishes the mutation, even if the live
    // snapshot arrived first. Never present stale proof as complete.
    const businessId = runtime.activeBusinessId;
    queueMicrotask(() => { if (runtime.activeBusinessId === businessId) void runtime.retryAccountingJournal?.(); });
    return { ...next, accountingEvents: [], accountingJournalStatus: { status: 'LOADING', event_count: 0 },
      accountingBasisCoverage: window.PlummeAccountingBasisCoverage.loadingCoverage() };
  };

  const loadSnapshot = async ({ readPage, businessId, year }) => {
    const events = [];
    const eventIds = new Set();
    let afterSeq = 0;
    let watermark = null;
    let sourceFingerprint = '';
    let accumulatedBytes = 0;

    for (let pageNumber = 1; pageNumber <= MAX_PAGES; pageNumber += 1) {
      const payload = await readPage({
        businessId,
        year,
        afterSeq,
        ...(watermark === null ? {} : { expectedWatermark: watermark, expectedSourceFingerprint: sourceFingerprint }),
      });
      if (text(payload?.businessId) !== text(businessId) || Number(payload?.year) !== Number(year)) throw new Error('Accounting journal page scope mismatch.');
      const page = payload?.journalPage;
      const pageEvents = Array.isArray(payload?.accountingEvents) ? payload.accountingEvents : [];
      if (!page || page.integrity !== 'COMPLETE' || Number(page.after_seq) !== afterSeq) {
        const error = new Error('Accounting journal page integrity failed.');
        error.reasonCodes = ['ACCOUNTING_JOURNAL_PAGE_INTEGRITY_FAILED', ...(Array.isArray(page?.reason_codes) ? page.reason_codes : [])]
          .filter(code => typeof code === 'string' && /^[A-Z][A-Z0-9_]{0,79}$/.test(code)).slice(0, 12);
        throw error;
      }
      if (pageEvents.length > MAX_EVENTS_PER_PAGE) throw new Error('Accounting journal page exceeded the client resource limit.');
      const nextWatermark = Number(page.taken_at_seq);
      const nextSourceFingerprint = text(payload?.sourceFingerprint);
      if (!Number.isInteger(nextWatermark) || nextWatermark < 0 || !nextSourceFingerprint) throw new Error('Accounting journal page proof is missing.');
      if (watermark !== null && (watermark !== nextWatermark || sourceFingerprint !== nextSourceFingerprint)) throw new Error('Accounting journal snapshot changed while paging.');
      watermark = nextWatermark;
      sourceFingerprint = nextSourceFingerprint;
      for (const event of pageEvents) {
        const sequence = Number(event?.recorded_seq);
        const eventId = text(event?.event_id);
        if (sequence !== afterSeq + 1 || !eventId || eventIds.has(eventId)) throw new Error('Accounting journal page sequence or identity is invalid.');
        afterSeq = sequence;
        eventIds.add(eventId);
        events.push(event);
      }
      accumulatedBytes += new TextEncoder().encode(JSON.stringify(pageEvents)).byteLength;
      if (accumulatedBytes > MAX_ACCUMULATED_BYTES) throw new Error('Accounting journal exceeded the client resource limit.');
      if (page.has_more) {
        if (!pageEvents.length || Number(page.last_seq) !== afterSeq) throw new Error('Accounting journal pagination made no progress.');
        continue;
      }
      if (afterSeq !== watermark || Number(page.last_seq) !== watermark) throw new Error('Accounting journal ended before its fixed watermark.');
      const finalized = await readPage({ businessId, year, finalize: true, expectedWatermark: watermark, expectedSourceFingerprint: sourceFingerprint });
      // Imported statistics are a readable report basis, not a complete native
      // journal. Keep the server classification and the normal consumer guards.
      const importedCoverage = globalThis.PlummeAccountingBasisCoverage?.acceptServerCoverage({
        coverage: finalized?.accountingBasisCoverage, responseBusinessId: finalized?.businessId,
        activeBusinessId: businessId, year, recordedSeq: watermark, eventCount: events.length,
      });
      const readableImport = importedCoverage === finalized?.accountingBasisCoverage
        && globalThis.PlummeAccountingBasisCoverage?.evaluate(importedCoverage, {
          allowImportedStatistics: true, period: { from: `${year}-01-01`, to: `${year}-12-31` },
        }).readable;
      const pendingOpening = importedCoverage === finalized?.accountingBasisCoverage
        && globalThis.PlummeAccountingBasisCoverage?.isImportedOpeningPending(importedCoverage);
      if (finalized?.finalized !== true || text(finalized?.businessId) !== text(businessId) || Number(finalized?.year) !== Number(year)
        || text(finalized?.sourceFingerprint) !== sourceFingerprint || (!['COMPLETE', 'EMPTY'].includes(finalized?.accountingBasisCoverage?.status) && !readableImport && !pendingOpening)) {
        // Expose bounded diagnostic codes, never business data or fingerprints.
        const codes = [];
        if (finalized?.finalized !== true) codes.push('FINALIZATION_NOT_CONFIRMED');
        if (text(finalized?.businessId) !== text(businessId)) codes.push('COVERAGE_BUSINESS_MISMATCH');
        if (Number(finalized?.year) !== Number(year)) codes.push('COVERAGE_YEAR_MISMATCH');
        if (text(finalized?.sourceFingerprint) !== sourceFingerprint) codes.push('COVERAGE_SOURCE_MISMATCH');
        if (!['COMPLETE', 'EMPTY'].includes(finalized?.accountingBasisCoverage?.status) && !readableImport) {
          codes.push('ACCOUNTING_BASIS_INCOMPLETE');
          codes.push(...(Array.isArray(importedCoverage?.reason_codes) ? importedCoverage.reason_codes : []));
        }
        const details = [...new Set(codes)].filter((code) => typeof code === 'string' && /^[A-Z][A-Z0-9_]{0,79}$/.test(code)).slice(0, 12);
        const error = new Error('Accounting journal server finalization failed.' + ` (${details.join(', ')})`);
        error.reasonCodes = details;
        throw error;
      }
      return {
        ...payload,
        accountingEvents: events,
        accountingJournalStatus: { ...payload.accountingJournalStatus, event_count: events.length },
        accountingBasisCoverage: finalized.accountingBasisCoverage,
        journalRead: { mode: 'server_finalized_paginated', pageCount: pageNumber, accumulatedBytes, sourceFingerprint },
      };
    }
    throw new Error('Accounting journal exceeded the maximum page count.');
  };

  const isSnapshotConflict = (error) => /ACCOUNTING_(?:JOURNAL_WATERMARK|COVERAGE_SOURCE)_CHANGED/.test(String(error?.message || ''));

  const load = async (options) => {
    for (let attempt = 0; ; attempt += 1) {
      try {
        return await loadSnapshot(options);
      } catch (error) {
        if (!isSnapshotConflict(error) || attempt >= SNAPSHOT_RETRY_DELAYS_MS.length) throw error;
        await new Promise((resolve) => setTimeout(resolve, SNAPSHOT_RETRY_DELAYS_MS[attempt]));
      }
    }
  };

  return { mergeReadProof, mergeMutationReadProof, load, reloadKey, limits: { maxPages: MAX_PAGES, maxEventsPerPage: MAX_EVENTS_PER_PAGE, maxAccumulatedBytes: MAX_ACCUMULATED_BYTES } };
})();

Object.assign(globalThis, { PlummeAccountingJournalPagination });
